// client/src/components/people/PersonForm.js
// The one create/edit form for a person, whatever their roles. Partnership
// Agreement and Licensed show only while RGA Partner is checked.
//
// `users` is the /api/users/relationship-owners list: deactivated users carry
// `inactive` and show only when already an owner.
import { useState } from 'react'
import { RGA_PARTNER, useRoleOptions } from '../../lib/roles'
import { EntityField } from '../EntityPicker'
import { ErrorBanner, TagInput } from './ui'
import OwnerMultiField from '../OwnerMultiField'
import api from '../../lib/api'
import { requestFirmCompletion } from '../FirmCompletionHost'

// "+ Firm" / "+ Email" / "+ Phone": the label's first letter lines up with
// the first character inside the fields above, and the "+" hangs in the
// margin to its left. inset = the field's left padding plus its border
// (.input: 12 + 1; EntityField: 10 + 1).
function AddButton({ label, onClick, inset = 13 }) {
  return (
    <button type="button" onClick={onClick}
      style={{ alignSelf: 'flex-start', background: 'none', border: 'none', cursor: 'pointer',
        padding: `2px 0 2px ${inset}px`, fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'inherit' }}>
      <span style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', right: '100%', paddingRight: 4 }}>+</span>{label}
      </span>
    </button>
  )
}

// A list of single-value inputs with "+ Email" / "+ Phone" to add another
// and ✕ to remove one (same pattern as "+ Firm").
function MultiInput({ label, addLabel, values, onChange, type = 'text', placeholder }) {
  return (
    <div className="input-group">
      <label className="input-label">{values.length > 1 ? `${label}s` : label}</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {values.map((v, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input className="input" type={type} value={v} placeholder={placeholder} style={{ flex: 1, minWidth: 0 }}
              onChange={e => onChange(values.map((x, j) => (j === i ? e.target.value : x)))} />
            {values.length > 1 && (
              <button type="button" className="btn-icon" aria-label={`Remove this ${label.toLowerCase()}`}
                title={`Remove this ${label.toLowerCase()}`}
                onClick={() => onChange(values.filter((_, j) => j !== i))}>✕</button>
            )}
          </div>
        ))}
        <AddButton label={addLabel} onClick={() => onChange([...values, ''])} />
      </div>
    </div>
  )
}

export default function PersonForm({ initial = {}, defaultTypes = [], firms, allContacts, users, tagSuggestions, onSubmit, onCancel, submitting, error }) {
  const { contactTypes } = useRoleOptions()
  const [form, setForm] = useState({
    name:               initial.name              ?? '',
    // Every firm, all equal, each with the person's title there; the first
    // is also saved as the primary firm and its title as the main title.
    firm_rows: (() => {
      const firms = [...(initial.firms ?? [])]
        .sort((a, b) => (b.firm_id === initial.primary_firm_id) - (a.firm_id === initial.primary_firm_id))
      const rows = firms.map(f => ({ firm_id: f.firm_id, title_role: f.title_role ?? '' }))
      if (!rows.length) rows.push({ firm_id: initial.primary_firm_id ?? '', title_role: initial.title_role ?? '' })
      return rows
    })(),
    contact_type:       initial.contact_type ?? defaultTypes,
    email:             (initial.email ?? []).length ? [...initial.email] : [''],
    phone:             (initial.phone ?? []).length ? [...initial.phone] : [''],
    introduced_by:      initial.introduced_by      ?? '',
    relationship_owner: [].concat(initial.relationship_owner ?? []),
    tags:               initial.tags               ?? [],
    notes:              initial.notes              ?? '',
    partnership_agreement: initial.partnership_agreement ?? false,
    is_licensed:           initial.is_licensed         ?? false,
  })
  const [err, setErr] = useState(null)
  const isPartner = form.contact_type.includes(RGA_PARTNER)

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); setErr(null) }

  function toggleType(t) {
    setForm(p => ({
      ...p,
      contact_type: p.contact_type.includes(t)
        ? p.contact_type.filter(x => x !== t)
        : [...p.contact_type, t],
    }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim()) return setErr('Name is required.')
    const payload = { name: form.name.trim() }
    // New firms (not yet in the CRM) are saved first with their name only,
    // so the person can be linked to them; the New Firm window then opens to
    // finish their details (FirmCompletionHost). Real ids go back into the
    // form so a retry after an error never creates a firm twice.
    let rows = form.firm_rows
    const newFirmIds = []
    try {
      rows = await Promise.all(form.firm_rows.map(async r => {
        if (!r.firm_id?.startsWith('new:')) return r
        const { data } = await api.post('/api/firms/pending', { name: r.firm_id.slice(4) })
        if (!data.data.firm_type?.length) newFirmIds.push(data.data.firm_id)
        return { ...r, firm_id: data.data.firm_id }
      }))
    } catch (err2) {
      return setErr(err2.response?.data?.error || err2.message)
    }
    if (rows !== form.firm_rows) setForm(p => ({ ...p, firm_rows: rows }))

    // Firm and title stay paired row by row; [] clears every firm.
    payload.firms = rows.filter(r => r.firm_id).map(r => ({ firm_id: r.firm_id, title_role: r.title_role.trim() }))
    payload.title_role = rows[0]?.title_role.trim() || null
    if (form.contact_type.length) payload.contact_type       = form.contact_type
    const emails = form.email.map(v => v.trim()).filter(Boolean)
    const phones = form.phone.map(v => v.trim()).filter(Boolean)
    payload.email = emails.length ? emails : null   // removing every one clears it
    payload.phone = phones.length ? phones : null
    payload.introduced_by   = form.introduced_by || null
    payload.relationship_owner = form.relationship_owner   // full list; [] clears it
    if (form.tags.length)        payload.tags                = form.tags
    payload.notes           = form.notes.trim() || null
    if (isPartner) {
      payload.partnership_agreement = form.partnership_agreement
      payload.is_licensed           = form.is_licensed
    }
    try {
      await onSubmit(payload)
      if (newFirmIds.length) requestFirmCompletion(newFirmIds)
    }
    catch (err2) { setErr(err2.response?.data?.error || err2.message) }
  }

  return (
    <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ErrorBanner msg={err || error} />

      {/* Name */}
      <div className="input-group">
        <label className="input-label">Name *</label>
        <input className="input" value={form.name} onChange={e => set('name', e.target.value)}
          placeholder="Full name" autoFocus />
      </div>

      {/* Firms, each with its own title (all equal; "+ Firm" adds a pair) */}
      <div className="input-group">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px', gap: 12 }}>
          <label className="input-label">{form.firm_rows.length > 1 ? 'Firms' : 'Firm'}</label>
          <label className="input-label">Title / Role</label>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {form.firm_rows.map((r, i) => {
            const setRow = patch => set('firm_rows', form.firm_rows.map((x, j) => (j === i ? { ...x, ...patch } : x)))
            return (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px', gap: 12, alignItems: 'center' }}>
                <div style={{ minWidth: 0 }}>
                  <EntityField kind="firm" records={firms} value={r.firm_id} deferCreate
                    onChange={id => setRow({ firm_id: id ?? '' })} />
                </div>
                <input className="input" value={r.title_role} onChange={e => setRow({ title_role: e.target.value })}
                  placeholder="e.g. Head of PE" />
                {form.firm_rows.length > 1 ? (
                  <button type="button" className="btn-icon" aria-label="Remove this firm and title" title="Remove this firm and title"
                    onClick={() => set('firm_rows', form.firm_rows.filter((_, j) => j !== i))}>✕</button>
                ) : <span />}
              </div>
            )
          })}
          <AddButton label="Firm" inset={11} onClick={() => set('firm_rows', [...form.firm_rows, { firm_id: '', title_role: '' }])} />
        </div>
      </div>

      {/* Contact Type */}
      <div className="input-group">
        <label className="input-label">Contact Type</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
          {contactTypes.map(t => {
            const checked = form.contact_type.includes(t)
            return (
              <label key={t} style={{
                display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
                fontSize: 12, padding: '5px 10px', borderRadius: 5,
                border: `1px solid ${checked ? 'var(--accent)' : 'var(--border)'}`,
                background: checked ? 'var(--accent-muted)' : 'transparent',
                color: checked ? 'var(--text-primary)' : 'var(--text-secondary)',
                transition: 'all 0.12s',
              }}>
                <input type="checkbox" checked={checked} onChange={() => toggleType(t)}
                  style={{ accentColor: 'var(--accent)', cursor: 'pointer' }} />
                {t}
              </label>
            )
          })}
        </div>
      </div>

      {/* Partnership Agreement + Licensed: RGA Partners only */}
      {isPartner && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="input-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={form.partnership_agreement}
                onChange={e => set('partnership_agreement', e.target.checked)}
                style={{ accentColor: 'var(--accent)', cursor: 'pointer', width: 16, height: 16 }} />
              Partnership Agreement
            </label>
          </div>
          <div className="input-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={form.is_licensed}
                onChange={e => set('is_licensed', e.target.checked)}
                style={{ accentColor: 'var(--accent)', cursor: 'pointer', width: 16, height: 16 }} />
              Licensed
            </label>
          </div>
        </div>
      )}

      {/* Email + Phone */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
        <MultiInput label="Email" addLabel="Email" type="email" placeholder="name@firm.com"
          values={form.email} onChange={v => set('email', v)} />
        <MultiInput label="Phone" addLabel="Phone" placeholder="+1 555 0100"
          values={form.phone} onChange={v => set('phone', v)} />
      </div>

      {/* Introduced By */}
      <div className="input-group">
        <label className="input-label">Introduced By</label>
        <EntityField kind="contact" records={allContacts} value={form.introduced_by}
          onChange={id => set('introduced_by', id ?? '')} />
      </div>

      {/* Relationship Owner — searchable, individuals only */}
      <div className="input-group">
        <label className="input-label">Relationship Owner</label>
        <OwnerMultiField owners={users} value={form.relationship_owner}
          onChange={ids => setForm(p => ({ ...p, relationship_owner: ids }))} />
      </div>

      {/* Tags */}
      <div className="input-group">
        <label className="input-label">Tags</label>
        <TagInput tags={form.tags} onChange={v => set('tags', v)} suggestions={tagSuggestions ?? []} />
      </div>

      {/* Notes */}
      <div className="input-group">
        <label className="input-label">Notes</label>
        <textarea className="textarea" rows={3} value={form.notes}
          onChange={e => set('notes', e.target.value)} placeholder="Internal notes..." />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 8,
        borderTop: '1px solid var(--border)', marginTop: 4 }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  )
}
